import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

import { useState } from 'react';

import { useProfile } from '@/hooks/useProfile';
import { useUpdateProfile } from '@/hooks/useUpdateProfile';
import { DISABILITY_OPTIONS, LANGUAGE_OPTIONS } from '@/constant';
export const FormSchema = Yup.object().shape({
    email: Yup.string().required('Email is required'),
    phone: Yup.string().required('Phone is required').min(10, 'Phone number must be at least 10 digits'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    gender: Yup.string().required('Gender is required'),
    countryName: Yup.string().required('Country is required'),
    province: Yup.string().required('Province is required'),
    languages: Yup.array().of(Yup.string()).min(1, 'Select at least one language').required('Languages are required'),
    disability: Yup.string().required('Disability is required'),
});
const YourInfo = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { updateProfile } = useUpdateProfile();

    const [loading, setLoading] = useState(false);
    const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

    // Use custom SWR hook for profile with automatic auth handling
    const { profile, isLoading, mutate: refreshProfile } = useProfile();


    // Get unique cities from zones

    const inputs = [

        {
            label: 'First Name',
            name: 'firstName',
            placeholder: 'Enter your first name',
            isRequired: true,
        },
        {
            label: 'Last Name',
            name: 'lastName',
            placeholder: 'Enter your last name',
            isRequired: true,
        },

        {
            label: 'Email Address',
            name: 'email',
            placeholder: 'Enter your Email Address',
            isRequired: true,
        },
        {
            label: 'Gender',
            name: 'gender',
            placeholder: 'Select your Gender',
            isRequired: true,
            type: 'select',
            options: [
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
                { label: 'Non-Binary', value: 'non_binary' },
                { label: 'Other', value: 'other' },
            ],
        },
        {
            label: 'Country',
            name: 'countryName',
            placeholder: 'Select your country',
            isRequired: true,
            type: 'select',
            options: [
                { label: 'Canada', value: 'Canada' },
            ],
        },
        {
            label: 'City/Province',
            name: 'province',
            placeholder: 'Select your City/Province',
            isRequired: true,
            type: 'select',
            options: [
                { label: 'Nova Scotia', value: 'nova_scotia' },
                { label: 'Newfoundland', value: 'new_foundland' },
            ],
        },
        {
            label: 'Phone',
            name: 'phone',
            placeholder: 'Enter your Phone No',
            isRequired: true,
            type: 'phone', // Special type for phone input
        },
        {
            label: 'Languages Spoken',
            name: 'languages',
            placeholder: 'Select languages you speak',
            isRequired: true,
            type: 'multiselect',
            options: LANGUAGE_OPTIONS,
        },
        {
            label: 'Disability Status',
            name: 'disability',
            placeholder: 'Select your Disability Status',
            isRequired: true,
            type: 'radio',
            options: DISABILITY_OPTIONS,
        }

    ];





    const form = state?.formData || {};
    // const fields = form?.fields || [];
    const handleBack = () => {
        // navigate('/forms', { replace: true });
        navigate(-1);
    };

    const handleSubmit = async (values: any) => {

        try {
            const { email, phone, countryName, province, ...rest } = values;
            setLoading(true);
            const finalPayload = {
                ...rest,
                address: {
                    state: province,
                    // }
                },
                country: {
                    ...values?.country,
                    name: values?.countryName

                    // currencyCode: "CAD",
                    // currencySymbol: "$",
                    // countryCode: "+1",
                },
            };
            const result = await updateProfile(finalPayload);
            if (result && result.success) {
                await refreshProfile();
                navigate('/forms', { replace: true });
            } else {
                console.error("Failed to send OTP, not opening dialog");
                alert("Failed to send OTP. Please try again.");

            }
        } catch (error) {
            console.error("Error during form submission:", error);
            alert("An error occurred. Please try again.");
        }
        finally {
            setLoading(false);
        }
    };




    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <p className="text-lg">Loading profile...</p>
                </div>
            </div>
        );
    }


    return (
        <>
            <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-slate-900 text-black dark:text-white">
                <div onClick={handleBack} className="cursor-pointer">
                    <ChevronLeft />
                </div>
                <div className="flex-1 text-center">
                    <h1 className='text-lg font-semibold'>{form?.header || "Your Info"}</h1>
                </div>
                <div className="w-6"></div>
            </div>
            <div className="p-4">

                {/* Formik Form */}
                <Formik
                    initialValues={{
                        email: profile?.email || '',
                        phone: profile?.phone || '',

                        firstName: profile?.firstName || '',
                        lastName: profile?.lastName || '',
                        gender: profile?.gender || '',
                        // countryName: profile?.country?.name || '',
                        countryName: profile?.country?.name[0].toUpperCase() + profile?.country?.name.slice(1).toLowerCase() || '',
                        // Store province into address.state, so read it from there as initial value
                        province: (profile as any)?.address?.state || '',
                        country: profile?.country || {},
                        languages: (profile as any)?.languages || [],
                        disability: (profile as any)?.disability || '',
                    }}
                    enableReinitialize
                    validationSchema={FormSchema}
                    onSubmit={handleSubmit}

                >
                    {({ handleChange, handleBlur, values, setFieldValue }) => (
                        <Form className="space-y-5">

                            {inputs.map((field) => (

                                <div key={field.name} className="flex flex-col">
                                    <label className="text-sm font-medium mb-1">
                                        {field.label}
                                        {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                                    </label>

                                    {/* Render Select, Phone, or Input */}
                                    {field.type === 'select' ? (
                                        <Popover open={!!openDropdowns[field.name]} onOpenChange={(open) => setOpenDropdowns((prev) => ({ ...prev, [field.name]: open }))}>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-full justify-between">
                                                    {field.options?.find((opt: any) => opt.value === values[field.name])?.label || field.placeholder}
                                                </Button>
                                            </PopoverTrigger>

                                            <PopoverContent className="w-full p-0">
                                                {field.options?.map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        className="w-full text-left px-4 py-2 hover:bg-muted"
                                                        onClick={() => { setFieldValue(field.name, opt.value); setOpenDropdowns((prev) => ({ ...prev, [field.name]: false })); }}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </PopoverContent>
                                        </Popover>

                                    ) : field.type === 'phone' ? (

                                        <InputGroup
                                            data-disabled={true}
                                        // className="shadow-none border-#e4e4e7 ring-0 focus-within:ring-0 focus-within:ring-offset-0 [&:has([data-slot=input-group-control]:focus)]:ring-0"
                                        >
                                            <InputGroupAddon className='pr-2 m-0'>
                                                <InputGroupText>+{values.country?.countryCode || '1'}</InputGroupText>
                                            </InputGroupAddon>

                                            <InputGroupInput
                                                disabled
                                                className='focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
                                                type="tel"
                                                name={field.name}
                                                placeholder="(123) 456-7890"
                                                value={values.phone}
                                                onChange={(e) => {
                                                    // Remove non-numeric characters except for formatting
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    // Limit to 10 digits
                                                    const limitedValue = value.slice(0, 10);
                                                    handleChange({
                                                        target: {
                                                            name: 'phone',
                                                            value: limitedValue
                                                        }
                                                    });
                                                }}
                                                onBlur={handleBlur}
                                                maxLength={14}
                                            />
                                        </InputGroup>
                                    ) : field.type === 'multiselect' ? (
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="justify-between">
                                                    <span className="truncate">
                                                        {Array.isArray(values[field.name]) && (values[field.name] as any[]).length > 0
                                                            ? field.options
                                                                ?.filter((opt: any) => (values[field.name] as any[])?.includes(opt.value))
                                                                .map((opt: any) => opt.label)
                                                                .join(', ')
                                                            : field.placeholder || 'Select'}
                                                    </span>
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-64">
                                                <div className="space-y-2">
                                                    {field.options?.map((opt: any) => {
                                                        const current = Array.isArray(values[field.name]) ? (values[field.name] as any[]) : [];
                                                        const checked = current.includes(opt.value);
                                                        return (
                                                            <div key={opt.value} className="flex items-center gap-2">
                                                                <Checkbox
                                                                    checked={checked}
                                                                    onCheckedChange={(isChecked) => {
                                                                        let next: any[];
                                                                        if (isChecked) {
                                                                            if (current.length >= 4) {
                                                                                return;
                                                                            }
                                                                            next = [...current, opt.value];
                                                                        } else {
                                                                            next = current.filter((v) => v !== opt.value);
                                                                        }
                                                                        setFieldValue(field.name, next);
                                                                    }}
                                                                />
                                                                <span className="text-sm">{opt.label}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    ) : field.type === 'radio' ? (
                                        <div className="grid grid-cols-2 gap-2">
                                            {field.options?.map((opt: any) => (
                                                <label key={opt.value} className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="radio"
                                                        name={field.name}
                                                        value={opt.value}
                                                        checked={values[field.name as keyof typeof values] === opt.value}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                    {opt.label}
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <Input
                                            type={field.name === 'email' ? 'email' : field.name === 'phone' ? 'number' : 'text'}
                                            disabled={field.name === 'email'}
                                            name={field.name}
                                            placeholder={field.placeholder}
                                            value={values[field.name as keyof typeof values]}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    )}

                                    {/* Error message */}
                                    <ErrorMessage
                                        name={field.name}
                                        component="span"
                                        className="text-xs text-red-500 mt-1"
                                    />
                                </div>
                            ))}


                            <Button className="w-full 0" type="submit"
                            //   onClick={handleSubmit}
                            >{loading ? "Loading..." : "Submit"}</Button>
                            {/* Dialog removed */}
                        </Form>
                    )}
                </Formik>
            </div>

        </>
    )
}

export default YourInfo;